import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPinned, CheckCircle2, RefreshCw, ExternalLink, Navigation, User as UserIcon, Radio, StopCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { errorMessage } from "@/lib/api";

/**
 * Reusable "customer location" card. Two modes:
 *
 * mode="customer" — used on Customer Dashboard. Big amber "Share My
 *                   Location" primary CTA; POSTs to /customer/my-location
 *                   (writes to every SHC id under this phone).
 * mode="staff"    — used on Office Staff Profile dialog. Read-first: if
 *                   the customer already shared, show a green "Shared by
 *                   Customer" tile with a Navigate button. Re-capture is
 *                   a small secondary link (staff visit).
 */
export default function LocationCard({ mode, leadId, location, onSaved, saveEndpoint }) {
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null); // {active, expires_at, minutes}
  const [liveTicker, setLiveTicker] = useState(Date.now());
  const sharedByCustomer = location?.captured_by_role === "customer";

  // Poll live-share status for customer mode
  useEffect(() => {
    if (mode !== "customer") return;
    let mounted = true;
    const fetchStatus = async () => {
      try { const r = await api.get("/customer/live-share/status"); if (mounted) setLive(r.data); } catch {}
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 30000);
    const tk = setInterval(() => setLiveTicker(Date.now()), 1000);
    return () => { mounted = false; clearInterval(iv); clearInterval(tk); };
  }, [mode]);

  const startLive = () => {
    if (!("geolocation" in navigator)) return toast.error("Geolocation not supported");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.post("/customer/live-share/start", { latitude: pos.coords.latitude, longitude: pos.coords.longitude, minutes: 15 });
          toast.success("🟢 Live location sharing started — 15 minutes");
          setLive({ ...r.data, active: true });
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setBusy(false); }
      },
      () => { setBusy(false); toast.error("Location permission denied"); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const stopLive = async () => {
    try { await api.post("/customer/live-share/stop"); setLive({ active: false }); toast.success("Live sharing stopped"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  // Continuously update live coords when active
  useEffect(() => {
    if (mode !== "customer" || !live?.active || !live.expires_at) return;
    if (new Date(live.expires_at) < new Date()) { setLive({ active: false }); return; }
    let watchId = null;
    if ("geolocation" in navigator && navigator.geolocation.watchPosition) {
      watchId = navigator.geolocation.watchPosition(
        async (pos) => { try { await api.post("/customer/live-share/update", { latitude: pos.coords.latitude, longitude: pos.coords.longitude }); } catch {} },
        () => {},
        { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 }
      );
    }
    return () => { if (watchId !== null) navigator.geolocation.clearWatch(watchId); };
  }, [mode, live?.active, live?.expires_at]);

  const liveMsLeft = live?.expires_at ? Math.max(0, new Date(live.expires_at) - Date.now()) : 0;
  const liveMinLeft = Math.floor(liveMsLeft / 60000);
  const liveSecLeft = Math.floor((liveMsLeft % 60000) / 1000);

  const capture = async (isRecapture = false) => {
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const url = saveEndpoint || (mode === "customer" ? "/customer/my-location" : `/staff/leads/${leadId}/location`);
          const r = await api.post(url, { latitude: lat, longitude: lng });
          toast.success(mode === "customer"
            ? "✅ Location shared successfully!"
            : `✅ Location saved: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          onSaved && onSaved(r.data.location);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setBusy(false); }
      },
      (err) => {
        setBusy(false);
        if (err.code === err.PERMISSION_DENIED) toast.error("❌ Location permission denied. Please enable GPS and try again.");
        else if (err.code === err.POSITION_UNAVAILABLE) toast.error("❌ Location unavailable — try moving to open sky.");
        else if (err.code === err.TIMEOUT) toast.error("❌ Location timed out — try again.");
        else toast.error("❌ Could not fetch location.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const openMap = () => {
    if (!location) return;
    window.open(`https://www.google.com/maps?q=${location.lat},${location.lng}`, "_blank", "noopener,noreferrer");
  };
  const openNav = () => {
    if (!location) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`, "_blank", "noopener,noreferrer");
  };

  const capturedAt = (() => {
    if (!location?.captured_at) return "";
    try { return new Date(location.captured_at).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  })();

  const wrapCls = mode === "customer"
    ? "rounded-2xl border-2 border-amber-500/50 bg-amber-500/5 p-5"
    : "rounded-lg border-2 border-amber-500/40 bg-amber-500/5 p-4";

  return (
    <div className={wrapCls} data-testid={`location-card-${mode}`}>
      <div className="flex items-center gap-2 mb-3">
        <MapPinned className="w-4 h-4 text-amber-400"/>
        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
          {sharedByCustomer ? "📍 Location Shared by Customer" : "📍 Customer Location"}
        </div>
      </div>

      {!location && mode === "customer" && (
        <>
          <div className="text-sm text-slate-300 mb-4">
            Share your current location so our bank staff can reach you easily for verification.
          </div>
          <Button
            data-testid="customer-share-location-btn"
            onClick={() => capture(false)}
            disabled={busy}
            className="w-full h-14 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full text-base disabled:opacity-60">
            {busy ? (<><RefreshCw className="w-5 h-5 mr-2 animate-spin"/> Getting your location…</>)
                  : (<><MapPinned className="w-5 h-5 mr-2"/> Share My Current Location</>)}
          </Button>
          <div className="text-[11px] text-slate-500 mt-2 text-center">Uses your phone's GPS · Nothing else stored.</div>

          {/* Live share (15 minutes) */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2 flex items-center gap-1.5"><Radio className="w-3 h-3"/> Live Location Share</div>
            {live?.active && liveMsLeft > 0 ? (
              <div className="space-y-2" data-testid="live-share-active">
                <div className="rounded-lg bg-orange-500/15 border border-orange-500/40 p-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"/>
                  <div className="text-xs text-orange-200 flex-1">Live for <b className="font-mono-alt">{liveMinLeft}:{String(liveSecLeft).padStart(2,"0")}</b></div>
                </div>
                <Button data-testid="stop-live-share" onClick={stopLive} variant="secondary" className="w-full h-10 bg-slate-800 hover:bg-red-500 hover:text-white text-slate-300 font-bold rounded-full text-xs">
                  <StopCircle className="w-3.5 h-3.5 mr-1.5"/> Stop Live Sharing
                </Button>
              </div>
            ) : (
              <Button data-testid="start-live-share" onClick={startLive} disabled={busy} className="w-full h-11 bg-orange-500 hover:bg-orange-400 text-white font-bold rounded-full text-sm disabled:opacity-60">
                <Radio className="w-4 h-4 mr-1.5"/> Share Live for 15 min
              </Button>
            )}
            <div className="text-[10px] text-slate-500 mt-1.5 text-center">Partner can watch you approach the office in real-time.</div>
          </div>
        </>
      )}

      {!location && mode === "staff" && (
        <>
          <div className="text-xs text-slate-400 mb-3">
            Customer has not shared their location yet. Ask them to open the tracking dashboard and tap
            <span className="text-amber-300 font-semibold"> Share My Current Location</span>. You can also capture from here (staff visit).
          </div>
          <Button
            data-testid="capture-location-btn"
            onClick={() => capture(false)}
            disabled={busy}
            variant="secondary"
            className="w-full h-10 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-full disabled:opacity-60">
            {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin"/> : <MapPinned className="w-4 h-4 mr-2"/>}
            Capture (Staff Visit)
          </Button>
        </>
      )}

      {location && (
        <div data-testid="location-saved" className="space-y-3">
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5"/>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-emerald-200">
                {sharedByCustomer ? "Shared by Customer" : "Location Saved"}
              </div>
              <div className="text-[11px] font-mono-alt text-emerald-100/80 mt-0.5">
                {typeof location.lat === "number" ? location.lat.toFixed(5) : location.lat},
                {" "}
                {typeof location.lng === "number" ? location.lng.toFixed(5) : location.lng}
              </div>
              {capturedAt && (
                <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1 flex-wrap">
                  <span>Captured {capturedAt}</span>
                  {location.captured_by_name && (
                    <span className="flex items-center gap-0.5">
                      · by <b className="text-slate-200 ml-0.5">{location.captured_by_name}</b>
                      {sharedByCustomer && <span className="inline-flex items-center gap-0.5 ml-1 text-emerald-300">(<UserIcon className="w-2.5 h-2.5"/>Customer)</span>}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              data-testid="view-on-map-btn"
              onClick={openMap}
              className="h-10 bg-blue-500 hover:bg-blue-400 text-white font-bold rounded-full text-xs">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5"/> View on Map
            </Button>
            {mode === "staff" ? (
              <Button
                type="button"
                data-testid="navigate-to-customer-btn"
                onClick={openNav}
                className="h-10 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full text-xs">
                <Navigation className="w-3.5 h-3.5 mr-1.5"/> Navigate
              </Button>
            ) : (
              <Button
                type="button"
                data-testid="update-location-btn"
                onClick={() => capture(true)}
                disabled={busy}
                variant="secondary"
                className="h-10 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-full text-xs disabled:opacity-60">
                {busy ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>}
                Update Location
              </Button>
            )}
          </div>
          {/* Secondary re-capture for staff — only when customer already shared */}
          {mode === "staff" && sharedByCustomer && (
            <button
              type="button"
              data-testid="recapture-staff-btn"
              onClick={() => capture(true)}
              disabled={busy}
              className="w-full text-[11px] text-slate-500 hover:text-slate-300 py-1.5 disabled:opacity-40">
              Re-capture (Staff Visit)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
